import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import ConfirmDialog from "../components/ConfirmDialog";
import ServiceCategoryModal from "../components/ServiceCategoryModal";
import Toast from "../components/Toast";
import { apiErrorMessage } from "../services/api";
import { deleteServiceCategory, fetchServiceCategories } from "../services/serviceService";

/**
 * How the business groups its services (BIZ-06). Groups nest one level, and the
 * order set here is the order the menu reads in.
 */
export default function ServiceCategories() {
  const { t, i18n } = useTranslation();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setCategories(await fetchServiceCategories());
    } catch (err) {
      setError(apiErrorMessage(err, t("serviceCategories.loadFailed")));
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load, i18n.language]);

  const confirmDelete = async () => {
    setDeleteError("");
    setDeleteBusy(true);
    try {
      await deleteServiceCategory(deleting.id);
      setToast({ type: "success", message: t("serviceCategories.deleted", { name: deleting.name }) });
      setDeleting(null);
      load();
    } catch (err) {
      setDeleteError(apiErrorMessage(err, t("serviceCategories.deleteFailed")));
    } finally {
      setDeleteBusy(false);
    }
  };

  // Only top-level groups render as rows; their children hang underneath.
  const roots = categories.filter((category) => !category.parent_id);

  const rowFor = (category, nested = false) => (
    <tr key={category.id}>
      <td className="px-4 py-3">
        <span className={nested ? "ms-6 text-ink" : "font-medium"}>
          {nested && <span className="me-2 text-ink-muted">└</span>}
          {category.name}
        </span>
        {!category.is_active && (
          <span className="ms-2 text-xs text-ink-2">{t("serviceCategories.inactive")}</span>
        )}
      </td>
      <td className="px-4 py-3 text-ink-2">{category.services_count ?? 0}</td>
      <td className="px-4 py-3">
        {category.is_visible_online ? (
          <span className="rounded-full bg-accent-wash px-3 py-1 text-xs text-accent-text">
            {t("serviceCategories.online")}
          </span>
        ) : (
          <span className="rounded-full bg-raised px-3 py-1 text-xs text-ink-2">
            {t("serviceCategories.hidden")}
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-ink-2" dir="ltr">
        {category.sort_order}
      </td>
      <td className="px-4 py-3 text-end">
        <button
          type="button"
          onClick={() => {
            setEditing(category);
            setModalOpen(true);
          }}
          className="text-sm font-semibold text-accent-text hover:underline"
        >
          {t("common.edit")}
        </button>
        <button
          type="button"
          onClick={() => {
            setDeleteError("");
            setDeleting(category);
          }}
          className="ms-3 text-sm font-semibold text-danger-text hover:underline"
        >
          {t("common.delete")}
        </button>
      </td>
    </tr>
  );

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">{t("serviceCategories.title")}</h1>
          <p className="text-sm text-ink-2">{t("serviceCategories.subtitle")}</p>
        </div>
        <div className="flex gap-2">
          <Link to="/services" className="btn-secondary">
            {t("serviceCategories.backToCatalogue")}
          </Link>
          <button
            type="button"
            onClick={() => {
              setEditing(null);
              setModalOpen(true);
            }}
            className="btn-primary"
          >
            {t("serviceCategories.new")}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
          {error}
        </div>
      )}

      <div className="card overflow-hidden">
        {loading ? (
          <p className="px-4 py-10 text-center text-sm text-ink-2">{t("common.loading")}</p>
        ) : roots.length === 0 ? (
          <p className="px-4 py-12 text-center text-sm text-ink-2">{t("serviceCategories.empty")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-start text-sm">
              <thead className="border-b border-line bg-raised text-xs uppercase tracking-wide text-ink-2">
                <tr>
                  <th className="px-4 py-3">{t("serviceCategories.table.name")}</th>
                  <th className="px-4 py-3">{t("serviceCategories.table.services")}</th>
                  <th className="px-4 py-3">{t("serviceCategories.table.visibility")}</th>
                  <th className="px-4 py-3">{t("serviceCategories.table.order")}</th>
                  <th className="px-4 py-3 text-end">{t("serviceCategories.table.actions")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {roots.flatMap((category) => [
                  rowFor(category),
                  ...(category.children ?? []).map((child) => rowFor(child, true)),
                ])}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ServiceCategoryModal
        category={editing}
        parents={roots}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={(saved, wasEditing) => {
          setModalOpen(false);
          setToast({
            type: "success",
            message: t(wasEditing ? "serviceCategories.updated" : "serviceCategories.created", {
              name: saved.name,
            }),
          });
          load();
        }}
      />

      <ConfirmDialog
        open={Boolean(deleting)}
        title={t("serviceCategories.deleteTitle")}
        message={t("serviceCategories.deleteMessage", { name: deleting?.name ?? "" })}
        error={deleteError}
        busy={deleteBusy}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
      />

      {toast && <Toast type={toast.type} message={toast.message} onDone={() => setToast(null)} />}
    </div>
  );
}
